'use client';

import React, { createContext, useEffect } from 'react';
import { usePathname } from 'next/navigation';

export type FullscreenContextData = {
  activated: boolean;
  deactivated: boolean;
  activate: () => void;
  deactivate: () => void;
  toggle: () => void;
};

const FullscreenContext = createContext({} as FullscreenContextData);

type FullscreenProviderProps = {
  children: React.ReactNode;
  startState?: boolean;
};

export function FullscreenProvider({
  children,
  startState = false,
}: FullscreenProviderProps) {
  const pathname = usePathname();

  const [activated, setActivated] = React.useState(startState);

  useEffect(() => {
    setActivated(false);
  }, [pathname]);

  function activate() {
    setActivated(true);
  }

  function deactivate() {
    setActivated(false);
  }

  function toggle() {
    setActivated(state => !state);
  }

  const providerValue = React.useMemo(
    () => ({
      activated,
      deactivated: !activated,
      activate,
      deactivate,
      toggle,
    }),
    [activated]
  );

  return (
    <FullscreenContext.Provider value={providerValue}>
      {children}
    </FullscreenContext.Provider>
  );
}

export function useFullscreen() {
  const context = React.useContext(FullscreenContext);

  if (!context) {
    throw new Error('useFullscreen must be used within a FullscreenProvider');
  }

  return context;
}
